/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import React from 'react'
import { RootSDKStoreType, TokenType } from '@vatom/sdk/core'

import CameraPlugin from './CameraPlugin'
import DebugPlugin from './DebugPlugin'
import DeviceMotionCheckPlugin from './DeviceMotionCheckPlugin'
import GeolocationPlugin from './GeolocationPlugin'
import HUDCompassPlugin from './HUDCompassPlugin'
import Plugin8thWall from './Plugin8thWall'
import Render3DPlugin from './Render3DPlugin'
import SimpleObjectPlugin from './SimpleObjectPlugin'
import TokenPlugin from './TokenPlugin'

type CenterProps = {
  style: any
  children: any
}

/** This component displays UI in the center of the container */
const Center = (props: CenterProps) => (
  <div
    style={Object.assign(
      {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      },
      props.style
    )}
  >
    <div>{props.children}</div>
  </div>
)

type Status = {
  id: string
  text: string
}

/** List of possible properties this component can be given */
interface ARProps {
  /** Vatom SDK */
  sdk: RootSDKStoreType

  /** List of vatoms to display in AR. */
  tokens?: TokenType[]

  /** If true, vatoms will be fetched from the map and displayed */
  fetchVatomsFromMap?: boolean

  /** If specified, runs a custom session managed by a remote script */
  // customSessionConfig?: CustomSessionConfig

  /** If specified, this action will be performed on the Vatom when it's tapped instead of the default action */
  onTokenSelect?: (token: TokenType) => void
}

/** Possible state values for this component */
type ARState = {
  isLoading?: boolean
  loadingProgress?: number
  loadingText?: string
  error?: {
    message: string
  } | null
  menuOpen?: boolean
  menuTarget?: Element | null
}

/** AR Viewer component for Web */
export class ARViewerWeb extends React.PureComponent<ARProps, ARState> {
  // Plugins
  plugins: any[] = []
  statuses: Status[]

  // Plugin references
  customSession?: any
  currentGeolocation?: GeolocationCoordinates
  tokenPlugin?: TokenPlugin
  simpleObjectPlugin?: SimpleObjectPlugin

  // For 8th Wall?
  camera: any
  scene: any
  renderer: any
  audioListener: any

  /** Static configuration */
  config = {
    /** 8th wall API key */
    appKey8thWall: 'q6f9KSydWOgg0tZAqX7lKHT1kXBa8GNvgQqwxk6UGTadk8Io1pqPBUMt9HTlTLJx4d49lR',
    // appKey8thWall: '',

    /** Maximum items to show */
    maxItems: 5,

    // Radius around the user to fetch vatoms, in meters
    visibleRadius: 500,

    // Should we move objects closer to / farther from the camera, and what distance multiplier should we use? 1 = off
    distanceMultiplier: 0.05,

    // Minimum distance from the camera, in meters. If an object is closer than this, it will be moved this far away.
    minDistance: 1,

    // A single FQDN to whitelist. This performs filtering server-side.
    fqdnWhitelist: '',

    // Attempt to synchronise the camera feed to the 3D renderer. Requires more processing power. Only applicable when not using 8th wall.
    cameraSync: false,

    // Display debug info in the AR feed.
    debugInfo: false, //DEBUG ? true : false,

    // Display a debug floor grid
    floorGrid: false,

    showRecord: true
  }

  /** Constructor */
  constructor(props: ARProps) {
    super(props)

    // Load 3D renderer plugins. Note, the order of plugin loading is important.

    // Make sure the device supports motion access
    this.plugins.push(new DeviceMotionCheckPlugin(this))

    // Check wether to use 8th wall or not
    if (this.config.appKey8thWall) {
      // Yes, use 8th wall to provide 6 degrees of freedom and camera feed
      this.plugins.push(new Plugin8thWall(this))
    } else {
      // No, just use THREE.js's camera orientation control to provide 3 degrees of freedom.
      this.plugins.push(new Render3DPlugin(this))

      // Display and optionally sync the camera feed as well
      this.plugins.push(new CameraPlugin(this))
    }

    // Fetch the device's location if necessary
    if (this.props.fetchVatomsFromMap) this.plugins.push(new GeolocationPlugin(this))

    // Fetch and render tokens nearby
    this.plugins.push(new TokenPlugin(this))

    // Fetch and render a simple object if defined
    this.plugins.push(new SimpleObjectPlugin(this))

    // Show a compass along the bottom of the screen
    this.plugins.push(new HUDCompassPlugin(this))

    // Show debug information if necessary
    if (this.getSetting('ar.debug-info')) this.plugins.push(new DebugPlugin(this))

    // Setup state
    this.state = {
      isLoading: true,
      loadingProgress: 0,
      loadingText: 'Loading plugins...',
      error: null,
      menuOpen: false,
      menuTarget: null
    }

    // List of current statuses, displayed while plugins are loading
    this.statuses = []
  }

  close() {
    // Check to see if this is a customSession with a token in a folder
    // const parentId = this.customSession?.token?.parentId;
    // if (parentId && parentId !== ".") {
    //     if (this.props.business) {
    //         // Go back to business page
    //         this.props.history.push(`/b/${this.props.business.name}/token/${parentId}`)
    //     } else {
    //         this.props.history.push(`/token/${parentId}`)
    //     }
    // } else if (this.props.business) {
    // 	// Go back to business page
    // 	this.props.history.push(`/b/${this.props.business.name}`);
    // } else {
    // 	this.props.history.push("/");
    // }
  }

  render() {
    const isGeoError =
      this.state.error &&
      (this.state.error.message === 'Origin does not have permission to use Geolocation service' ||
        this.state.error.message === 'User denied Geolocation')
    const isCameraError =
      this.state.error && this.state.error.message === 'Please check your camera permissions.'
    const isGenericError = this.state.error && this.state.error.message === 'Permission denied'

    let cameraErrorMsg = 'SETTINGS -> SAFARI -> CAMERA, AND ALLOW CAMERA ACCESS.'
    let geoErrorMsg =
      'SETTINGS -> PRIVACY -> LOCATION SERVICES -> SAFARI WEBSITES, AND ALLOW ACCESS.'

    if (navigator.userAgent.includes('Chrome/')) {
      cameraErrorMsg = 'SETTINGS -> APPS -> CHROME -> PERMISSIONS, AND ALLOW CAMERA ACCESS.'
      geoErrorMsg = 'SETTINGS -> APPS -> CHROME -> PERMISSIONS, AND ALLOW LOCATION ACCESS.'
    } else if (navigator.userAgent.includes('Firefox/')) {
      cameraErrorMsg = 'SETTINGS -> APPS -> FIREFOX -> PERMISSIONS, AND ALLOW CAMERA ACCESS.'
      geoErrorMsg = 'SETTINGS -> APPS -> FIREFOX -> PERMISSIONS, AND ALLOW LOCATION ACCESS.'
    }
    const headerColor = this.customSession?.config?.header?.color || '#FFFFFF'
    sessionStorage.setItem('hudcolor', headerColor)

    // Render UI components
    return (
      <div
        ref={elem => this.receiveDiv(elem)}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#444',
          fontFamily: 'Inter, "Helvetica Neue", Helvetica, Arial, sans-serif'
        }}
      >
        {/* Display message if still loading */}
        {this.state.isLoading ? (
          <Center
            style={{
              zIndex: 5,
              backgroundColor: '#25272b',
              textAlign: 'center'
            }}
          >
            {/* Loading icon */}
            <div
              style={{
                width: 128,
                height: 128,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundImage: 'url(' + require('./loading.svg') + ')',
                margin: 'auto',
                marginBottom: 60
              }}
            />

            {/* Progress bar */}
            <div
              style={{
                position: 'relative',
                width: 300,
                height: 8,
                borderRadius: 4,
                overflow: 'hidden',
                backgroundColor: '#333333',
                background: 'linear-gradient(to bottom, #333333 0%, #4d4d4d 100%)',
                marginBottom: 6
              }}
            >
              {/* Progress bar inner bar */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: Math.floor(this?.state?.loadingProgress ?? 0 * 100) + '%',
                  height: '100%',
                  borderRadius: 4,
                  transition: 'width 0.25s',
                  backgroundColor: '#23538a',
                  background: 'linear-gradient(to bottom, #a7cfdf 0%,#23538a 100%)'
                }}
              />
            </div>

            {/* Loading text */}
            <div
              style={{
                fontSize: '0.706rem',
                fontStyle: 'italic',
                color: '#AAA',
                textAlign: 'center'
              }}
            >
              {this.state.loadingText}
            </div>
          </Center>
        ) : null}

        {/* Display error message if needed */}
        {/* Display error message if needed */}
        {this.state.error && !isGeoError && !isCameraError ? (
          <Center style={{ zIndex: 10, backgroundColor: '#25272b' }}>
            <div style={{ textAlign: 'center' }}>
              <img
                alt="warning"
                src={undefined /*require("../../res/warning.svg").default*/}
                style={{ width: 48, height: 48 }}
              />
            </div>
            <div
              style={{
                padding: 20,
                paddingBottom: 0,
                color: '#FFF',
                fontSize: 17,
                textAlign: 'center'
              }}
            >
              Augmented Reality Error
            </div>
            <div
              style={{
                padding: 20,
                paddingTop: 8,
                color: '#DDD',
                fontSize: 15,
                textAlign: 'center',
                whiteSpace: 'pre-wrap'
              }}
            >
              {this.state.error.message}
            </div>

            {/* If on Chrome on iOS, show Open in Safari button */}
            {navigator.userAgent.includes('CriOS/') ? (
              <div
                style={{
                  padding: 20,
                  paddingTop: 8,
                  margin: '0px 40px',
                  color: '#DDD',
                  fontSize: 15,
                  textAlign: 'center'
                }}
              >
                <a
                  href={window.location.href}
                  style={{ color: '#49E' }}
                  onClick={() => copyToClipboard(window.location.href)}
                >
                  Tap here
                </a>{' '}
                to copy the URL, and then paste it into the address bar in Safari.
              </div>
            ) : null}
          </Center>
        ) : null}

        {isGenericError && (
          <Center style={{ zIndex: 10, backgroundColor: '#004b87' }}>
            <div
              style={{
                padding: 20,
                paddingBottom: 0,
                color: '#FFF',
                fontSize: 17,
                textAlign: 'center'
              }}
            >
              THIS EXPERIENCE REQUIRES ACCESS TO YOUR CAMERA AND LOCATION SERVICES WHICH MAY HAVE
              BEEN DISABLED ON YOUR DEVICE.
            </div>
            <div
              style={{
                padding: 20,
                paddingBottom: 0,
                color: '#FFF',
                fontSize: 17,
                textAlign: 'center'
              }}
            >
              PLEASE GO TO:
            </div>
            <div
              style={{
                padding: 20,
                paddingTop: 8,
                color: '#DDD',
                fontSize: 15,
                textAlign: 'center',
                whiteSpace: 'pre-wrap'
              }}
            >
              {cameraErrorMsg}
            </div>
            <div
              style={{
                padding: 20,
                paddingTop: 8,
                color: '#DDD',
                fontSize: 15,
                textAlign: 'center',
                whiteSpace: 'pre-wrap'
              }}
            >
              {geoErrorMsg}
            </div>
          </Center>
        )}

        {isCameraError && (
          <Center style={{ zIndex: 10, backgroundColor: '#004b87' }}>
            <div
              style={{
                padding: 20,
                paddingBottom: 0,
                color: '#FFF',
                fontSize: 17,
                textAlign: 'center'
              }}
            >
              THIS EXPERIENCE REQUIRES ACCESS TO YOUR CAMERA WHICH MAY HAVE BEEN DISABLED ON YOUR
              DEVICE.
            </div>
            <div
              style={{
                padding: 20,
                paddingBottom: 0,
                color: '#FFF',
                fontSize: 17,
                textAlign: 'center'
              }}
            >
              PLEASE GO TO:
            </div>
            <div
              style={{
                padding: 20,
                paddingTop: 8,
                color: '#DDD',
                fontSize: 15,
                textAlign: 'center',
                whiteSpace: 'pre-wrap'
              }}
            >
              {cameraErrorMsg}
            </div>
          </Center>
        )}

        {this.state.error && isGeoError && (
          <Center style={{ zIndex: 10, backgroundColor: '#004b87' }}>
            <div
              style={{
                padding: 20,
                paddingBottom: 0,
                color: '#FFF',
                fontSize: 17,
                textAlign: 'center'
              }}
            >
              THIS EXPERIENCE REQUIRES LOCATION SERVICES WHICH MAY HAVE BEEN DISABLED ON YOUR
              DEVICE.
            </div>
            <div
              style={{
                padding: 20,
                paddingBottom: 0,
                color: '#FFF',
                fontSize: 17,
                textAlign: 'center'
              }}
            >
              PLEASE GO TO:
            </div>
            <div
              style={{
                padding: 20,
                paddingTop: 8,
                color: '#DDD',
                fontSize: 15,
                textAlign: 'center',
                whiteSpace: 'pre-wrap'
              }}
            >
              {geoErrorMsg}
            </div>
          </Center>
        )}

        {/* Close button */}
        {/* <div
					style={{
						position: "absolute",
						top: 10,
						left: 20,
						width: 64,
						height: 64,
						color: headerColor,
						cursor: "pointer",
						zIndex: 20,
						fontSize: 40,
					}}
					onClick={(e) => this.close()}
				>
					<i className="fal fa-times"></i>
				</div> */}

        {/* Display first status */}
        <div
          style={{
            display: this.statuses.length > 0 && !this.state.error ? 'block' : 'none',
            position: 'absolute',
            zIndex: 10,
            bottom: 20,
            left: 0,
            width: '100%',
            textAlign: 'center'
          }}
        >
          <div
            style={{
              display: 'inline-block',
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              padding: '10px 20px',
              color: 'white',
              fontSize: '0.706rem',
              textAlign: 'center',
              borderRadius: 5
            }}
          >
            {this.statuses[0] && this.statuses[0].text}
          </div>
        </div>

        {/* Refresh position button */}
        {/* <div style={{ display: this.statuses.length == 0 && !this.state.error ? 'block' : 'none', position: 'absolute', zIndex: '10', bottom: 20, left: 0, width: '100%', textAlign: 'center' }}>
          <div onClick={this.refreshPosition.bind(this)} style={{ display: 'inline-block', backgroundColor: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)', padding: '10px 20px', color: '#08F', cursor: 'pointer', fontSize: '0.706rem', textAlign: 'center', borderRadius: 5 }}>
           Refresh Position
          </div>
        </div> */}

        {/* Menu */}
        <div
          tabIndex={0}
          role="button"
          style={{
            position: 'absolute',
            top: 10,
            left: 64,
            width: 64,
            height: 64,
            color: headerColor,
            cursor: 'pointer',
            zIndex: 10,
            fontSize: 40
          }}
          onKeyDown={e => this.setState({ menuTarget: e.currentTarget })}
          onClick={() => null}
        >
          <i className="fal fa-ellipsis-h"></i>
        </div>
        <div style={{ position: 'absolute', top: 0, left: 64, zIndex: 10 }}>
          {/* <Menu anchorEl={this.state.menuTarget} open={!!this.state.menuTarget} onClose={e => this.setState({ menuTarget: null })}>
                    <MenuItem>
                Debug Menu
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={e => this.toggleSetting('ar.debug-info')}>
                        {'Show debug stats: ' + (this.getSetting('ar.debug-info') ? 'Enabled' : 'Disabled')}
                    </MenuItem>
                    <MenuItem onClick={e => this.toggleSetting('ar.camera-sync')}>
                        {'Sync camera feed: ' + (this.getSetting('ar.camera-sync') ? 'Enabled' : 'Disabled')}
                    </MenuItem>
                    <MenuItem onClick={e => this.toggleSetting('ar.floor-grid')}>
                        {'Floor grid: ' + (this.getSetting('ar.floor-grid') ? 'Enabled' : 'Disabled')}
                    </MenuItem>
                    <MenuItem style={{whiteSpace: 'break-spaces'}}>
                        {this.state.error ? this.state.error.toString() : ""}
                    </MenuItem>
                </Menu> */}
        </div>
      </div>
    )
  }

  /** The DIV element for non-React plugins to use */
  div?: HTMLDivElement

  /** Called when we receive the div element */
  async receiveDiv(elem: any) {
    // Stop if we already have the element
    if (this.div || !elem) return

    // Store it
    this.div = elem as HTMLDivElement

    // Start loading
    this.setState({ error: null, isLoading: true })
    try {
      // Load components
      for (const plugin of this.plugins) {
        console.log('Loading plugin: ', plugin.name || plugin.constructor.name)
        await plugin.load(this.div)
      }

      // Notify plugins load has completed
      for (const plugin of this.plugins)
        if (plugin.loadComplete) await plugin.loadComplete(this.div)

      // Done
      this.setState({ isLoading: false })
    } catch (err: any) {
      // Failed! Show error
      this.setState({ error: err as Error, isLoading: false })
      console.error(err)

      // Errors.show(err)
      console.error('Error: ' + err.message)
    }
  }

  // shouldComponentUpdate(nextProps: any, nextState: any) {
  // 	console.info("In shouldComponentUpdate")
  // 	return false
  // }

  /** Called when component is being removed */
  componentWillUnmount() {
    // Unload components
    for (const plugin of this.plugins) plugin.unload()
  }

  /** Called when component is updated */
  componentDidUpdate() {
    // Stop if still loading
    if (this.state.isLoading) return

    // Update components
    for (const plugin of this.plugins) plugin.componentDidUpdate?.()
  }

  /** Add or update a status */
  setStatus(id: string, text: string) {
    // Remove existing
    this.statuses = this.statuses.filter(s => s.id !== id)

    // Add new
    this.statuses.push({ id, text })

    // Update UI
    this.forceUpdate()
  }

  /** Clear a status */
  clearStatus(id: string) {
    // Remove existing
    this.statuses = this.statuses.filter(s => s.id !== id)

    // Update UI
    this.forceUpdate()
  }

  /** User wants to refresh the position */
  refreshPosition() {
    // Find geolocation plugin
    const plugin = this.plugins.find(p => p instanceof GeolocationPlugin)

    // Refresh
    plugin.updateLocation()
  }

  /** Toggle a global setting */
  toggleSetting(name: string) {
    // Change the setting
    localStorage[name] = !this.getSetting(name) ? 'on' : 'off'

    // Reload
    window.location.reload()
  }

  /** Get setting value */
  getSetting(name: string) {
    // Get it
    const value = localStorage[name]

    // Return defaults if needed
    if (typeof value === 'undefined') {
      if (name === 'ar.debug-info') return this.config.debugInfo
      if (name === 'ar.camera-sync') return this.config.cameraSync
      if (name === 'ar.floor-grid') return this.config.floorGrid
    }

    // If it's boolean, return that
    if (value === 'on') return true
    if (value === 'off') return false

    // Just return it
    return value
  }

  /** Ask the user to enter a text value setting */
  askSetting(name: string, description: string) {
    // Ask user
    const value = prompt(description, this.getSetting(name))
    if (!value) return

    // Set setting
    localStorage[name] = value

    // Reload
    window.location.reload()
  }
}

/** Called when the user clicks the Copy button next to the Ethereum address */
export const copyToClipboard = (str: string) => {
  // Create fake input field - From https://hackernoon.com/copying-text-to-clipboard-with-javascript-df4d4988697f
  const field = document.createElement('textarea')
  field.value = str
  document.body.appendChild(field)
  field.select()
  document.execCommand('copy')
  document.body.removeChild(field)

  // Done
  // toast.success('Copied to clipboard')
}

// const Wrapper = (props: any) => {
// 	const bus = useBusiness();
// 	const dataPool = useDataPool();
// 	const { isLoading } = useIsLoading("inventory", true);
// 	const c = getUrlParameter("c");
// 	// If CustomSession is active, wait for inventory to load
// 	if (c && isLoading) return <Loader isLoading />;

// 	return <AR {...props} business={bus} dataPool={dataPool} />;
// };

export default ARViewerWeb //withRouter(Wrapper);
